import { useDispatch, useStore } from 'react-redux';

import { ReduxStateHandler } from 'connect/state/ReduxStateHandler';
import { RecursivePartial } from 'types/RecursivePartial';

interface UsePatchProps<T> {
  selector?: (state: any) => T;
  action?: (value: RecursivePartial<T>) => { type: string; payload: T; };
  stateHandler?: ReduxStateHandler<T>;
}

/**
 * Creates a function that updates part of a redux state object without overwriting the entire object.
 *
 * @param stateHandler - The state handler to use
 * @param selector - Manually choose a selector function to get the state
 * @param action - Manually choose an action function to update the state
 *
 * @returns a function that updates the state with the provided payload
 *
 * @example
 * const patchCheckout = usePatch<CheckoutState>({
 *   stateHandler: CheckoutStateHandler
 * });
 *
 * patchCheckout({
 *   info: {
 *     email: 'test@test.com',
 *     phoneNumber: '1234567890',
 *     promotionalSmsConsent: true,
 *   },
 * });
 */
export function usePatch<T>({ selector, action, stateHandler }: UsePatchProps<T>) {
  const selectorToUse = stateHandler?.selector || selector;
  const actionToUse = stateHandler?.update || action;

  if (!selectorToUse && !actionToUse) {
    throw new Error('Either stateHandler or selector and action must be provided');
  }

  const dispatch = useDispatch();
  const store = useStore();

  // Return a function that can be called later with the payload
  return (payload: RecursivePartial<T>) => {
    // We need to call store.getState directly since we can't call useSelector
    // from within the returned function
    const state = selectorToUse!(store.getState());

    dispatch(actionToUse!({
      ...state,
      ...payload,
    }));
  };
}
